import { useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { verstuurSoepFeedback } from '@/lib/ai';
import type { FeedbackReden } from '@/lib/api-types';

/**
 * Een duim omhoog of omlaag bij het gegenereerde verslag.
 *
 * WAAROM DIT ZO KLEIN IS
 *
 * Dit scherm heeft één doel: het verslag controleren en versturen. Feedback is
 * waardevol, maar niet waardevol genoeg om daartussen te gaan staan -- een balk die
 * om aandacht vraagt bij elk consult wordt binnen een week weggeklikt zonder gelezen
 * te worden, en dan is de opbrengst nul én is het scherm trager geworden. Twee
 * pictogrammen in de voettekst, in dezelfde toon als de kostenregel ernaast.
 *
 * WAAROM DE DUIM METEEN WORDT VERSTUURD
 *
 * Een oordeel dat pas telt ná het invullen van een reden is een oordeel dat je
 * grotendeels kwijtraakt: de meeste mensen klikken de duim en gaan door. De klik
 * gaat daarom onmiddellijk naar de server, en de reden komt als tweede aanroep bij
 * dezelfde rij. Bedenkt iemand zich, dan overschrijft de volgende klik het oordeel;
 * er is er één per generatie.
 *
 * WAAROM ER GEEN FOUTMELDING KOMT
 *
 * `verstuurSoepFeedback` gooit niet. Als het vastleggen mislukt, is dat vervelend,
 * maar het mag geen toast opleveren over een scherm waar een nog niet verzonden
 * SOEP-verslag in staat -- de gebruiker was met iets anders bezig en kan er niets
 * mee. Het lukt of het lukt niet; de bevestiging past zich aan.
 */

/** De redenen bij een duim omlaag. Gelijk aan FEEDBACK_REDENEN in de API. */
const REDENEN: Array<{ waarde: FeedbackReden; label: string }> = [
  { waarde: 'onjuist', label: 'Onjuiste inhoud' },
  { waarde: 'onvolledig', label: 'Mist informatie' },
  { waarde: 'indeling', label: 'Verkeerde SOEP-indeling' },
  { waarde: 'te_lang', label: 'Te uitgebreid' },
  { waarde: 'te_kort', label: 'Te beknopt' },
  { waarde: 'taal', label: 'Taal of stijl' },
  { waarde: 'anders', label: 'Anders' },
];

interface SoepFeedbackProps {
  /**
   * De generatie waar dit oordeel over gaat, uit het antwoord van /ai/soep.
   *
   * Ontbreekt hij, dan komt het verslag niet uit een generatie die de server kent --
   * dat kan bij een scherm dat nog openstond over een sessiewissel heen. Dan is er
   * niets om een oordeel aan te hangen en toont dit component niets, in plaats van
   * een knop die stilletjes niets doet.
   */
  generationId: string | null;
}

export function SoepFeedback({ generationId }: SoepFeedbackProps) {
  const [oordeel, setOordeel] = useState<'up' | 'down' | null>(null);
  const [redenOpen, setRedenOpen] = useState(false);
  const [tags, setTags] = useState<FeedbackReden[]>([]);
  const [toelichting, setToelichting] = useState('');
  const [bezig, setBezig] = useState(false);
  const [redenVerstuurd, setRedenVerstuurd] = useState(false);
  const [mislukt, setMislukt] = useState(false);

  if (!generationId) return null;

  const kiesOordeel = (waarde: 'up' | 'down') => {
    setOordeel(waarde);
    setMislukt(false);
    // Bij een duim omlaag komt het redenpaneel open; bij omhoog is er niets meer te
    // vragen. Wie van omlaag naar omhoog gaat, sluit het paneel weer.
    setRedenOpen(waarde === 'down');
    if (waarde === 'up') setRedenVerstuurd(false);

    void verstuurSoepFeedback({ generationId, rating: waarde }).then((gelukt) => {
      if (!gelukt) setMislukt(true);
    });
  };

  const wisselTag = (waarde: FeedbackReden) => {
    setTags((vorige) =>
      vorige.includes(waarde) ? vorige.filter((t) => t !== waarde) : [...vorige, waarde],
    );
  };

  const verstuurReden = async () => {
    setBezig(true);
    const gelukt = await verstuurSoepFeedback({
      generationId,
      rating: 'down',
      reasonTags: tags,
      ...(toelichting.trim() ? { reason: toelichting.trim() } : {}),
    });
    setBezig(false);
    setMislukt(!gelukt);
    if (gelukt) {
      setRedenVerstuurd(true);
      setRedenOpen(false);
    }
  };

  return (
    <div className="px-1">
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
        <span>Bruikbaar verslag?</span>

        <DuimKnop
          actief={oordeel === 'up'}
          label="Verslag was bruikbaar"
          onClick={() => kiesOordeel('up')}
          variant="up"
        />
        <DuimKnop
          actief={oordeel === 'down'}
          label="Verslag was niet bruikbaar"
          onClick={() => kiesOordeel('down')}
          variant="down"
        />

        {/* De bevestiging staat naast de duimen en niet in een toast: een melding
            die over het scherm schuift hoort bij iets wat misging, niet bij een klik
            waar de gebruiker geen gevolg van hoeft te merken. */}
        {mislukt ? (
          <span className="italic">Beoordeling niet opgeslagen</span>
        ) : oordeel === 'up' || redenVerstuurd ? (
          <span>Bedankt voor de terugkoppeling.</span>
        ) : null}

        {oordeel === 'down' && !redenOpen && !redenVerstuurd && (
          <button
            type="button"
            className="underline underline-offset-2 hover:text-foreground"
            onClick={() => setRedenOpen(true)}
          >
            Reden toevoegen
          </button>
        )}
      </div>

      {redenOpen && (
        <div className="mt-1.5 rounded-md border border-border bg-muted/30 p-2 space-y-1.5">
          <div className="flex flex-wrap gap-1">
            {REDENEN.map((reden) => {
              const gekozen = tags.includes(reden.waarde);
              return (
                <button
                  key={reden.waarde}
                  type="button"
                  aria-pressed={gekozen}
                  onClick={() => wisselTag(reden.waarde)}
                  className={`rounded px-1.5 py-0.5 text-[10px] border transition-colors ${
                    gekozen
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-foreground border-border hover:border-primary/50'
                  }`}
                >
                  {reden.label}
                </button>
              );
            })}
          </div>

          <Textarea
            value={toelichting}
            onChange={(e) => setToelichting(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="Toelichting (optioneel) — geen patiëntgegevens"
            className="text-[11px] min-h-[2.5rem] resize-none bg-background"
          />

          {/* De waarschuwing staat er niet voor de vorm: dit veld gaat naar het
              beheerscherm van iSOFT en niet naar het dossier, en wat een gebruiker
              typt terwijl het consult nog op het scherm staat kan van alles zijn. */}
          <p className="text-[10px] text-muted-foreground">
            Deze toelichting gaat naar het beheer van iSOFT. Vermeld er geen gegevens in
            waarmee de patiënt te herleiden is.
          </p>

          <div className="flex justify-end gap-1.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-[10px]"
              onClick={() => setRedenOpen(false)}
            >
              Overslaan
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-6 text-[10px]"
              disabled={bezig || (tags.length === 0 && toelichting.trim().length === 0)}
              onClick={() => void verstuurReden()}
            >
              Versturen
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DuimKnop({
  actief,
  label,
  onClick,
  variant,
}: {
  actief: boolean;
  label: string;
  onClick: () => void;
  variant: 'up' | 'down';
}) {
  const Icoon = variant === 'up' ? ThumbsUp : ThumbsDown;
  const actiefKleur =
    variant === 'up'
      ? 'text-primary bg-primary/10 border-primary/30'
      : 'text-destructive bg-destructive/10 border-destructive/30';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actief}
      aria-label={label}
      title={label}
      className={`inline-flex items-center justify-center h-5 w-5 rounded border transition-colors ${
        actief ? actiefKleur : 'border-transparent hover:bg-muted hover:text-foreground'
      }`}
    >
      <Icoon className="h-3 w-3" />
    </button>
  );
}
